import { type ElementType, useMemo } from 'react';

import { useDesignContext } from '../providers';
import Pagination from './Pagination';
import { useTranslation } from '../hooks/useTranslation';
import { IconErrorBoundary } from './Icon';

type Props<T> = {
  loading?: boolean;
  elements: T[] | undefined;
  currentPage?: number;
  pages?: number;
  onPageChange?: (page: number) => void;
  HeaderComponent?: ElementType;
  RowComponent: ElementType;
  identifier?: string;
  onReload?: () => void;
};

export function ListPaginated<T = unknown>({
  loading = false,
  elements = [],
  currentPage,
  pages,
  onPageChange = () => null,
  HeaderComponent,
  RowComponent,
  identifier = 'id',
  onReload = () => null
}: Props<T>) {
  const { t } = useTranslation();
  const { Loader } = useDesignContext();

  const PagesComponent = useMemo(() => {
    if (pages && currentPage) {
      return <Pagination pages={pages} page={currentPage} onPageChange={onPageChange} />;
    } else {
      return undefined;
    }
  }, [currentPage, onPageChange, pages]);
  if (loading) {
    return <Loader />;
  }
  return (
    <div className="d-flex flex-column justify-content-between flex-grow-1">
      <div className="row gx-2">
        {HeaderComponent && <HeaderComponent />}
        {!elements.length && <>{t('No data available to display')}.</>}
        {elements.map((element) => (
          <IconErrorBoundary>
            <RowComponent key={element[identifier]} {...element} onReload={onReload} />
          </IconErrorBoundary>
        ))}
      </div>
      {PagesComponent}
    </div>
  );
}

export default ListPaginated;
